import { useState } from 'react';
import { Plus, Search, Edit2, Trash2, PawPrint } from 'lucide-react';
import { useLabSpecies, useCreateLabSpecies, useUpdateLabSpecies, useDeleteLabSpecies } from '@/hooks/useLab';
import { LabSpecies } from '@/types';
import { LabSpeciesCreate } from '@/services/lab';

export default function LabSpeciesPage() {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState<LabSpecies | null>(null);
  const [deletingSpecies, setDeletingSpecies] = useState<LabSpecies | null>(null);
  const [formData, setFormData] = useState<LabSpeciesCreate>({
    name: '',
    scientific_name: '',
    description: '',
  });

  const { data: species = [], isLoading, refetch } = useLabSpecies({
    search: search || undefined,
    is_active: true,
  });
  const createMutation = useCreateLabSpecies();
  const updateMutation = useUpdateLabSpecies();
  const deleteMutation = useDeleteLabSpecies();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSpecies) {
        await updateMutation.mutateAsync({
          id: editingSpecies.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (sp: LabSpecies) => {
    setEditingSpecies(sp);
    setFormData({
      name: sp.name,
      scientific_name: sp.scientific_name || '',
      description: sp.description || '',
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deletingSpecies) return;
    await deleteMutation.mutateAsync(deletingSpecies.id);
    setDeletingSpecies(null);
    refetch();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSpecies(null);
    setFormData({
      name: '',
      scientific_name: '',
      description: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Especies</h1>
          <p className="text-gray-600">Gestionar especies de laboratorio</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-lab-600 text-white px-4 py-2 rounded-lg hover:bg-lab-700"
        >
          <Plus className="h-5 w-5" />
          Nueva Especie
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
          />
        </div>
      </div>

      {/* Content - Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : species.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <PawPrint className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No hay especies registradas</p>
            <p className="text-sm mt-2">Agregue especies para clasificar los pacientes del laboratorio</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Nombre</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Nombre Cientifico</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Descripcion</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {species.map((sp) => (
                  <tr key={sp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-lab-100 rounded-lg">
                          <PawPrint className="h-4 w-4 text-lab-600" />
                        </div>
                        <span className="font-medium text-gray-900">{sp.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 italic">{sp.scientific_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{sp.description || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleEdit(sp)}
                          className="p-1.5 text-lab-600 hover:bg-lab-50 rounded"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeletingSpecies(sp)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingSpecies && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Especie</h3>
              <p className="text-gray-600">
                ¿Estas seguro de que queres eliminar <span className="font-semibold">{deletingSpecies.name}</span>? Esta accion no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setDeletingSpecies(null)}
                className="flex-1 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingSpecies ? 'Editar Especie' : 'Nueva Especie'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Nombre de la especie"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Cientifico</label>
                <input
                  type="text"
                  value={formData.scientific_name}
                  onChange={(e) => setFormData({ ...formData, scientific_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Ej: Canis lupus familiaris"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Descripcion opcional..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-lab-600 text-white rounded-lg hover:bg-lab-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
